import { useEffect, useMemo, useState } from "react";
import { Download, Eye, Printer, X, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { mediaUrl } from "@/lib/api";
import { prod, errMsg } from "@/lib/prodApi";
import { buildPacks, renderAssets, PROD_ONLY } from "@/components/production/printKit";
import { usePrintJob } from "@/components/production/usePrintJob";
import { labelsPack } from "@/components/production/labels";

function Preview({ asset, onClose, onPrint, onPdf }) {
  return (
    <div className="fixed inset-0 z-50 bg-[#383f43]/95 overflow-auto" data-testid="print-preview">
      <div className="sticky top-0 z-10 bg-white border-b border-[#b1b4b6]">
        <div className="site-container flex items-center gap-4 py-4">
          <h2 className="text-2xl font-black" data-testid="print-preview-title">{asset.title}</h2>
          <span className="text-[#505a5f]">A4 portrait · 100% scale · actual size</span>
          <button className="btn-ghost ml-auto" onClick={onPrint} data-testid="preview-print-button"><Printer size={18} /> Print</button>
          <button className="btn-ghost" onClick={onPdf} data-testid="preview-pdf-button"><Download size={18} /> Download PDF</button>
          <button className="btn-spore" onClick={onClose} data-testid="preview-close-button"><X size={18} /> Close</button>
        </div>
      </div>
      <div className="preview-stage py-10" data-testid="print-preview-pages">{renderAssets([asset])}</div>
    </div>
  );
}

function AssetRow({ a, n, run, busy }) {
  return (
    <li className="flex items-center gap-4 px-6 py-4 border-t border-[#b1b4b6]" data-testid={`asset-${a.id}`}>
      <span className="step-num !w-9 !h-9 !text-lg">{n}</span>
      <div className="flex-1">
        <div className="text-xl font-bold" data-testid={`asset-${a.id}-title`}>{a.title}</div>
        <div className="text-[16px] text-[#505a5f]">A4 portrait · 100% scale</div>
        {a.note && <strong className="tag bg-[#ffdd00] text-[#0b0c0c] mt-1.5" data-testid={`asset-${a.id}-crew-note`}>Crew: {a.note}</strong>}
      </div>
      <button className="btn-ghost !py-2 !text-[17px]" onClick={() => run("preview", [a])} data-testid={`asset-${a.id}-preview`}><Eye size={17} /> Preview</button>
      <button className="btn-ghost !py-2 !text-[17px]" disabled={busy} onClick={() => run("print", [a], a.title)} data-testid={`asset-${a.id}-print`}><Printer size={17} /> Print</button>
      <button className="btn-ghost !py-2 !text-[17px]" disabled={busy} onClick={() => run("pdf", [a], a.title)} data-testid={`asset-${a.id}-pdf`}><Download size={17} /> Download PDF</button>
      {a.docx && <a className="btn-ghost !py-2 !text-[17px]" href={mediaUrl(a.docx)} download data-testid={`asset-${a.id}-docx`}><FileText size={17} /> Download Word</a>}
    </li>
  );
}

function PackCard({ pack, run, busy }) {
  const whole = pack.whole ? [pack.whole] : pack.assets;
  return (
    <section className={`panel ${pack.kind === "production" ? "border-[#d4351c] border-2" : ""}`} data-testid={`pack-${pack.id}`}>
      <div className="flex items-center gap-4 px-6 py-5">
        <div className="flex-1">
          <h3 className="text-2xl font-black" data-testid={`pack-${pack.id}-title`}>{pack.title}</h3>
          <div className="text-[16px] text-[#505a5f]">{pack.note || `Contents: ${pack.assets.length} assets, in print order`}</div>
          {pack.kind === "production" && <strong className="tag bg-[#d4351c] text-white mt-2" data-testid="production-only-tag">{PROD_ONLY}</strong>}
        </div>
        <button className="btn-ghost" disabled={busy} onClick={() => run("print", whole, pack.title)} data-testid={`pack-${pack.id}-print`}><Printer size={18} /> {pack.printLabel || "Print pack"}</button>
        <button className="btn-spore" disabled={busy} onClick={() => run("pdf", whole, pack.title, pack.file)} data-testid={`pack-${pack.id}-pdf`}><Download size={18} /> {pack.pdfLabel || "Download PDF"}</button>
      </div>
      <ol>{pack.assets.map((a, i) => <AssetRow key={a.id} a={a} n={i + 1} run={run} busy={busy} />)}</ol>
    </section>
  );
}

export default function PrintLibrary() {
  const [content, setContent] = useState(null);
  const { job, start, root } = usePrintJob();
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    prod.get("/print/content").then((r) => setContent(r.data)).catch((e) => toast.error(errMsg(e, "Print library unavailable")));
  }, []);
  const packs = useMemo(() => (content ? [...buildPacks(content), labelsPack(content)] : []), [content]);

  const run = (mode, assets, title, file) => {
    if (mode === "preview") return setPreview(assets[0]);
    return start(mode, assets, title, file);
  };
  if (!content) return <p className="text-lg py-8">Loading print library…</p>;
  const suspects = packs.filter((p) => p.kind === "suspect");
  const all = packs.flatMap((p) => (p.whole ? [p.whole] : p.assets));

  return (
    <div data-testid="print-library">
      <div className="flex flex-wrap items-end gap-4 mb-8">
        <div className="flex-1 min-w-[420px]">
          <h2 className="text-4xl font-black">Print library</h2>
          <p className="text-lg text-[#505a5f] mt-2">Every asset prints on A4 portrait at 100% scale. Text is taken from the same source as the game.</p>
        </div>
        <button className="btn-ghost" disabled={!!job} onClick={() => run("pdf", suspects.flatMap((p) => p.assets), "All suspect packs", "All_suspect_packs")} data-testid="download-all-suspect-packs"><Download size={18} /> Download all suspect packs</button>
        <button className="btn-spore" disabled={!!job} onClick={() => run("pdf", all, "Everything", "Everything")} data-testid="download-everything"><Download size={18} /> Download everything</button>
      </div>
      {job && (
        <div className="banner mb-6" data-testid="print-job-status">
          <div className="banner-head flex items-center gap-2"><Loader2 size={18} className="animate-spin" /> {job.mode === "pdf" ? "Producing PDF" : "Preparing print"}: {job.title}</div>
        </div>
      )}
      <div className="space-y-8">{packs.map((p) => <PackCard key={p.id} pack={p} run={run} busy={!!job} />)}</div>
      {preview && <Preview asset={preview} onClose={() => setPreview(null)} onPrint={() => run("print", [preview], preview.title)} onPdf={() => run("pdf", [preview], preview.title)} />}
      {root}
    </div>
  );
}
